/**
 * Landing v3 — the agent chat's typing (landing-agent-chat): a request types
 * itself into the composer, holds, is wiped, and the next one types in —
 * round and round. Pure, so the rhythm is pinned by a test.
 */

export type TypingPhase = 'typing' | 'holding' | 'deleting' | 'pausing';

export type TypingState = {
  /** The request on screen. */
  index: number;
  /** How much of it is typed. */
  length: number;
  phase: TypingPhase;
};

/** A key a second, give or take: a person typing, not a printer. */
const TYPE_MS = [42, 58, 36, 64, 48, 40, 70, 44] as const;
/** Long enough to read a request once it is whole. */
const HOLD_MS = 1900;
/** The wipe: quicker than the typing, as a held backspace is. */
const DELETE_MS = 16;
/** A breath before the next request. */
const PAUSE_MS = 420;

export const TYPING_START: TypingState = { index: 0, length: 0, phase: 'typing' };

/** A request typed from its first character (a shortcut picked it). */
export const typingFrom = (index: number): TypingState => ({
  index,
  length: 0,
  phase: 'typing',
});

/**
 * One step of the loop, for requests of these `lengths`: the next state and
 * how long to wait before taking it.
 */
export function typingStep(
  state: TypingState,
  lengths: readonly number[],
): { next: TypingState; delay: number } {
  const full = lengths[state.index] ?? 0;
  switch (state.phase) {
    case 'typing':
      if (state.length < full) {
        return {
          next: { ...state, length: state.length + 1 },
          delay: TYPE_MS[state.length % TYPE_MS.length],
        };
      }
      return { next: { ...state, phase: 'holding' }, delay: HOLD_MS };
    case 'holding':
      return { next: { ...state, phase: 'deleting' }, delay: DELETE_MS };
    case 'deleting':
      if (state.length > 0) {
        return { next: { ...state, length: state.length - 1 }, delay: DELETE_MS };
      }
      return { next: { ...state, phase: 'pausing' }, delay: PAUSE_MS };
    case 'pausing':
      return {
        next: typingFrom((state.index + 1) % Math.max(1, lengths.length)),
        delay: TYPE_MS[0],
      };
  }
}
